export const calcServeSide = score => (
  score % 2 === 0 ? 'right' : 'left'
);

export const getCapScore = points => {
  if (points === 21) return 30;
  if (points === 11) return 21;
  return points + 9;
};

export const checkWin = (scoreA, scoreB, points) => {
  const highest = Math.max(scoreA, scoreB);
  const lowest = Math.min(scoreA, scoreB);

  if (highest < points) return null;
  if (highest - lowest >= 2) return scoreA > scoreB ? 'A' : 'B';
  if (highest >= getCapScore(points)) return scoreA > scoreB ? 'A' : 'B';

  return null;
};

export const getMidPoint = points => Math.ceil(points / 2);

export const shouldMidGameSwap = (
  scoreA,
  scoreB,
  previousScoreA,
  previousScoreB,
  points,
  game,
  bestOf
) => {
  if (bestOf < 2 || game !== bestOf) return false;

  const midpoint = getMidPoint(points);
  const previousHighest = Math.max(previousScoreA, previousScoreB);
  const currentHighest = Math.max(scoreA, scoreB);

  return previousHighest < midpoint && currentHighest >= midpoint;
};

export const getPreviousGameWinner = state => {
  if (state.scoreA > state.scoreB) return 'A';
  if (state.scoreB > state.scoreA) return 'B';
  return null;
};

export const getAlertLabel = (
  scoreA,
  scoreB,
  gamesA,
  gamesB,
  points,
  bestOf
) => {
  const cap = getCapScore(points);
  const gamesNeeded = Math.ceil(bestOf / 2);

  const isGamePoint = (mine, opponent) => {
    if (mine < points - 1) return false;
    if (mine >= points && mine - opponent === 1) return true;
    if (mine === points - 1 && opponent < points - 1) return true;
    return mine >= cap - 1 && mine - opponent >= 0;
  };

  return {
    alertA: isGamePoint(scoreA, scoreB)
      ? gamesA + 1 >= gamesNeeded ? '賽末分' : '局末分'
      : null,
    alertB: isGamePoint(scoreB, scoreA)
      ? gamesB + 1 >= gamesNeeded ? '賽末分' : '局末分'
      : null
  };
};

export const makeInitState = ({
  teamNameA,
  teamNameB,
  playerNameA,
  playerNameB,
  points,
  pts,
  bestOf
}) => ({
  teamNameA,
  teamNameB,
  playerNameA,
  playerNameB,
  pts: points ?? pts,
  bestOf,
  scoreA: 0,
  scoreB: 0,
  gamesA: 0,
  gamesB: 0,
  serving: 'A',
  serveSide: 'right',
  swapped: false,
  game: 1,
  status: 'playing',
  history: [],
  redoStack: [],
  log: [],
  pointLog: [],
  allPointLogs: []
});

const snapshot = state => {
  const {history, redoStack, ...rest} = state;
  return JSON.parse(JSON.stringify(rest));
};

const TAG_EMOJI = {
  winner: '🏸',
  error: '😬',
  serve_fault: '⚡',
  receive_fault: '🫳',
  out: '↗️',
  net: '🥅'
};

const handlePoint = (state, team) => {
  if (state.status !== 'playing') return state;

  const previous = snapshot(state);
  const scoreA = state.scoreA + (team === 'A' ? 1 : 0);
  const scoreB = state.scoreB + (team === 'B' ? 1 : 0);
  const midGameSwap = shouldMidGameSwap(
    scoreA,
    scoreB,
    state.scoreA,
    state.scoreB,
    state.pts,
    state.game,
    state.bestOf
  );
  const serverScore = team === 'A' ? scoreA : scoreB;
  const winner = checkWin(scoreA, scoreB, state.pts);
  const scorerName = team === 'A' ? state.teamNameA : state.teamNameB;
  const pointEntry = {
    scorer: team,
    server: state.serving,
    tagId: null,
    scoreA,
    scoreB
  };

  let next = {
    ...state,
    scoreA,
    scoreB,
    serving: team,
    serveSide: calcServeSide(serverScore),
    swapped: midGameSwap ? !state.swapped : state.swapped,
    redoStack: [],
    lastScoringTeam: team,
    log: [
      ...state.log,
      `${scorerName} 得分 → ${scoreA} : ${scoreB}`,
      ...(midGameSwap
        ? [`↔ 決勝局中途換邊（任一方先到 ${getMidPoint(state.pts)} 分）`]
        : [])
    ],
    pointLog: [...(state.pointLog || []), pointEntry],
    history: [...state.history.slice(-49), previous]
  };

  if (!winner) return next;

  const gamesA = state.gamesA + (winner === 'A' ? 1 : 0);
  const gamesB = state.gamesB + (winner === 'B' ? 1 : 0);
  const gamesNeeded = Math.ceil(state.bestOf / 2);
  const matchOver = gamesA >= gamesNeeded || gamesB >= gamesNeeded;

  next = {
    ...next,
    gamesA,
    gamesB,
    status: matchOver ? 'match_over' : 'game_over',
    log: [
      ...next.log,
      matchOver ? '🏆 比賽結束' : `🎯 第 ${state.game} 局結束`
    ],
    allPointLogs: [...(state.allPointLogs || []), next.pointLog]
  };

  return next;
};

const handleNextGame = state => {
  if (state.status !== 'game_over') return state;

  const previousGameWinner = getPreviousGameWinner(state);
  if (!previousGameWinner) return state;

  const previous = snapshot(state);
  const nextGame = state.game + 1;

  return {
    ...state,
    scoreA: 0,
    scoreB: 0,
    game: nextGame,
    swapped: !state.swapped,
    serving: previousGameWinner,
    serveSide: 'right',
    status: 'playing',
    redoStack: [],
    pointLog: [],
    history: [...state.history.slice(-49), previous],
    log: [
      ...state.log,
      '↔ 局間換邊',
      `─── 第 ${nextGame} 局開始，由 ${
        previousGameWinner === 'A' ? state.teamNameA : state.teamNameB
      } 發球 ───`
    ]
  };
};

export const matchReducer = (state, action) => {
  switch (action.type) {
    case 'POINT':
      return handlePoint(state, action.team);

    case 'TAG_POINT': {
      const pointLog = [...(state.pointLog || [])];
      if (!pointLog.length) return state;

      pointLog[pointLog.length - 1] = {
        ...pointLog[pointLog.length - 1],
        tagId: action.tagId
      };

      const log = [...state.log];
      for (let index = log.length - 1; index >= 0; index -= 1) {
        if (log[index].includes('得分 →') && !log[index].includes('[')) {
          log[index] = `${log[index]} [${
            TAG_EMOJI[action.tagId] || action.tagId
          }]`;
          break;
        }
      }

      return {...state, pointLog, log};
    }

    case 'UNDO': {
      if (!state.history.length) return state;
      const previous = state.history[state.history.length - 1];

      return {
        ...previous,
        history: state.history.slice(0, -1),
        redoStack: [...(state.redoStack || []), snapshot(state)],
        log: [...(previous.log || []), '↩ 撤銷上一個操作']
      };
    }

    case 'REDO': {
      if (!state.redoStack?.length) return state;
      const next = state.redoStack[state.redoStack.length - 1];

      return {
        ...next,
        history: [...state.history, snapshot(state)],
        redoStack: state.redoStack.slice(0, -1),
        log: [...(next.log || []), '↪ 恢復上一個操作']
      };
    }

    case 'LET':
      return {...state, log: [...state.log, '🔄 Let — 重打（分數不變）']};

    case 'DISPUTE': {
      if (!state.history.length) {
        return {
          ...state,
          log: [
            ...state.log,
            `⚖️ 裁判改判 — 由 ${
              action.newServer === 'A' ? state.teamNameA : state.teamNameB
            } 發球`
          ]
        };
      }

      const previous = state.history[state.history.length - 1];

      return {
        ...previous,
        serving: action.newServer,
        serveSide: calcServeSide(
          action.newServer === 'A' ? previous.scoreA : previous.scoreB
        ),
        history: state.history.slice(0, -1),
        redoStack: [],
        log: [
          ...(previous.log || []),
          `⚖️ 裁判改判 — 撤銷並由 ${
            action.newServer === 'A' ? state.teamNameA : state.teamNameB
          } 重新發球`
        ]
      };
    }

    case 'NEXT_GAME':
      return handleNextGame(state);

    case 'SWAP': {
      const previous = snapshot(state);
      return {
        ...state,
        swapped: !state.swapped,
        redoStack: [],
        history: [...state.history.slice(-49), previous],
        log: [...state.log, '↔ 手動換邊']
      };
    }

    case 'RESTART_MATCH':
      return makeInitState(state);

    default:
      return state;
  }
};